import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ArchiveBoxIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Badge from '@/components/ui/Badge';
import Dropdown from '@/components/ui/Dropdown';
import EmptyState from '@/components/ui/EmptyState';
import Pagination from '@/components/ui/Pagination';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useDebounce } from '@/lib/useDebounce';
import { useFormats, useRaceTypes } from '@/features/reference/useReference';
import type { PlanListItem } from '@/api/plansService';
import { usePlans, PlanStatus } from './usePlans';
import { planCycleColor } from './planColors';
import PlanDeleteModal from './PlanDeleteModal';
import BackupPlansModal from './BackupPlansModal';
import { PLAN_TYPE_LABELS } from './planAllocation';


const STATUS_COLOR: Record<PlanStatus, 'green' | 'yellow' | 'gray'> = {
  Publié: 'green',
  Brouillon: 'yellow',
  Archivé: 'gray',
};

const CompositionBar = ({ plan }: { plan: PlanListItem }) => {
  const total = plan.composition.reduce((sum, c) => sum + c.weeks, 0) || 1;
  if (plan.composition.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex h-2.5 w-40 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
      {plan.composition.map((seg, i) => (
        <div
          key={`${seg.cycleId}-${seg.orderIndex}`}
          title={`${seg.name} · ${seg.weeks} semaines max${seg.role === 'RECOVERY' ? ' · récupération' : ''}`}
          style={{ width: `${(seg.weeks / total) * 100}%`, backgroundColor: planCycleColor(i) }}
        />
      ))}
    </div>
  );
};

const PlansPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [format, setFormat] = useState('all');
  const [raceType, setRaceType] = useState('all');
  const { data: formats = [] } = useFormats();
  const { data: raceTypes = [] } = useRaceTypes();
  const raceTypeFilters = useMemo(
    () => [{ value: 'all', label: 'Tous types' }, ...raceTypes.map((rt) => ({ value: rt.identifier, label: rt.label }))],
    [raceTypes]
  );
  const selectedRaceType = raceTypes.find((rt) => rt.identifier === raceType);
  // v7: the format chips follow the race type picked; Débutant has none.
  const formatFilters = useMemo(
    () =>
      selectedRaceType && selectedRaceType.identifier !== 'BEGINNER'
        ? [
            { value: 'all', label: 'Tous' },
            ...formats.filter((f) => f.raceTypeId === selectedRaceType.id).map((f) => ({ value: f.identifier, label: f.label })),
          ]
        : [],
    [formats, selectedRaceType]
  );
  const [backupOpen, setBackupOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<PlanListItem | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, format, raceType]);

  const filters = useMemo(
    () => ({ search: debouncedSearch || undefined, format, raceType, page }),
    [debouncedSearch, format, raceType, page]
  );

  const {
    items,
    total,
    pageSize,
    kpis,
    isLoading,
    deletePlan,
    isArchived,
    toggleArchive,
    statusOf,
  } = usePlans(filters);

  return (
    <>
      <PageHeader
        title="Plans d'entraînement"
        description="Entrainement / Plans"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBackupOpen(true)}>
              Plans d'attente et de récupération
            </Button>
            <Button onClick={() => navigate('/plans/create')}>+ Ajouter un plan</Button>
          </div>
        }
      />
      <BackupPlansModal open={backupOpen} onClose={() => setBackupOpen(false)} />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Plans publiés</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.published ?? '—'}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Brouillons</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.drafts ?? '—'}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Utilisateurs actifs</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.activeUsers ?? '—'}</p>
        </Card>
      </div>

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-72">
            <Input
              label="Rechercher"
              placeholder="Nom du plan"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-700">
            {raceTypeFilters.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  setRaceType(f.value);
                  setFormat('all');
                }}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  raceType === f.value
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          {formatFilters.length > 0 && (
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-700">
            {formatFilters.map((f) => (
              <button
                key={f.value}
                onClick={() => setFormat(f.value)}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  format === f.value
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          )}
        </div>
      </Card>

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && items.length === 0 && (
        <EmptyState
          icon={<RectangleStackIcon />}
          title="Aucun plan"
          description="Créez un plan pour séquencer des cycles selon un format."
          action={<Button onClick={() => navigate('/plans/create')}>+ Ajouter un plan</Button>}
        />
      )}

      {!isLoading && items.length > 0 && (
        <Card className="p-0">
          <Table>
            <THead>
              <tr>
                <TH>Nom du plan</TH>
                <TH>Type de course</TH>
                <TH>Format</TH>
                <TH>Cycles</TH>
                <TH>Composition</TH>
                <TH>Générable</TH>
                <TH>Statut</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {items.map((plan) => {
                const status = statusOf(plan);
                return (
                  <TR key={plan.id}>
                    <TD className="font-bold text-gray-900 dark:text-gray-100">
                      <button
                        type="button"
                        onClick={() => navigate(`/plans/edit/${plan.id}`)}
                        className="text-left hover:text-brand-600 dark:hover:text-brand-300"
                      >
                        {plan.displayName}
                      </button>
                      {!plan.isGeneratable && (
                        <Badge color="red" className="ml-2 font-medium">
                          À corriger
                        </Badge>
                      )}
                      <span className="block text-xs font-normal text-gray-400">
                        {plan.dminWeeks === plan.dmaxWeeks ? `${plan.dmaxWeeks} sem.` : `${plan.dminWeeks}–${plan.dmaxWeeks} sem.`}
                      </span>
                    </TD>
                    <TD className="text-gray-500">
                      {plan.raceTypeLabel}
                      <span className="block text-xs text-gray-400">{PLAN_TYPE_LABELS[plan.planType] ?? plan.planType}</span>
                    </TD>
                    <TD>
                      {plan.formatSlug ? (
                        <Badge color="blue" className="font-semibold">
                          {plan.formatSlug}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TD>
                    <TD className="text-gray-500">{plan.cyclesCount}</TD>
                    <TD>
                      <CompositionBar plan={plan} />
                    </TD>
                    <TD>
                      {plan.generable ? (
                        <Badge color="green">✓</Badge>
                      ) : (
                        <Badge color="red">✕ {plan.issueCount}</Badge>
                      )}
                    </TD>
                    <TD>
                      <Badge color={STATUS_COLOR[status]} className="font-medium">
                        {status}
                      </Badge>
                    </TD>
                    <TD className="text-right">
                      <Dropdown
                        actions={[
                          {
                            label: 'Modifier',
                            icon: <PencilIcon className="h-4 w-4" />,
                            onClick: () => navigate(`/plans/edit/${plan.id}`),
                          },
                          {
                            label: 'Dupliquer',
                            icon: <DocumentDuplicateIcon className="h-4 w-4" />,
                            onClick: () => navigate(`/plans/create?from=${plan.id}`),
                          },
                          {
                            label: isArchived(plan.id) ? 'Désarchiver' : 'Archiver',
                            icon: <ArchiveBoxIcon className="h-4 w-4" />,
                            onClick: () => toggleArchive(plan.id),
                          },
                          {
                            label: 'Supprimer',
                            icon: <TrashIcon className="h-4 w-4" />,
                            destructive: true,
                            onClick: () => setDeleteTarget(plan),
                          },
                        ]}
                      />
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </Card>
      )}

      <PlanDeleteModal
        plan={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDelete={async (id) => {
          await deletePlan(id);
          setDeleteTarget(null);
        }}
        onArchive={(id) => {
          toggleArchive(id);
          setDeleteTarget(null);
        }}
      />
    </>
  );
};

export default PlansPage;
